import React from 'react';
import {
  elbowConnector,
  pointsToPath,
  polylineLength,
  type Point,
} from '@/app/components/divergenceGeometry';

/**
 * The elbow line from the hero ring: a 1px path with one corner, ending in a
 * small plotted dot. It is what ties a label to the thing it describes.
 *
 * Self-contained — it renders its own <svg> with a viewBox derived from the two
 * endpoints — so a caller positions it like any other block and never has to
 * share a coordinate system with it.
 *
 * The draw-in is a CSS animation on a stroke dash, defined once in
 * tailwind.css and disabled under prefers-reduced-motion. No JavaScript runs
 * for it, which is why this stays a server component.
 */

export interface ConnectorProps {
  from: Point;
  to: Point;
  /** Length of the first, straight run before the corner. */
  elbowRun?: number;
  onDark?: boolean;
  /** Draw the line in on first paint. Off for anything that repeats down a
   *  page, where a dozen animating lines is noise rather than craft. */
  animate?: boolean;
  /** Seconds to wait before drawing, for staggering a set of them. */
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
}

const PADDING = 4;

export default function Connector({
  from,
  to,
  elbowRun = 46,
  onDark,
  animate = false,
  delay = 0,
  className,
  style,
}: ConnectorProps) {
  const points = elbowConnector(from, to, elbowRun);
  if (!points) return null;

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs) - PADDING;
  const minY = Math.min(...ys) - PADDING;
  const width = Math.max(...xs) - minX + PADDING;
  const height = Math.max(...ys) - minY + PADDING;

  const path = pointsToPath(points);
  const length = polylineLength(points);
  const end = points[points.length - 1];
  const channel = onDark ? 'var(--m-on-ink-rgb)' : 'var(--m-ink-rgb)';

  return (
    <svg
      className={className}
      style={style}
      width={width}
      height={height}
      viewBox={`${minX} ${minY} ${width} ${height}`}
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={path}
        stroke={`rgba(${channel},0.35)`}
        strokeWidth={1}
        className={animate ? 'instrument-draw' : undefined}
        style={
          animate
            ? {
                strokeDasharray: length,
                strokeDashoffset: length,
                animationDelay: `${delay}s`,
              }
            : undefined
        }
      />
      <circle cx={end.x} cy={end.y} r={2} fill={`rgba(${channel},0.55)`} />
    </svg>
  );
}
